import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { setPersistence, browserLocalPersistence, browserSessionPersistence,
  signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, updateProfile } from "firebase/auth";
import { X, Building2, User, MapPin, ArrowRight, AlertTriangle, CheckCircle2, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";
import { auth, FIREBASE_READY } from "../lib/firebase";
import { SOCIALPULSE_SSO_READY, startSocialPulseLogin } from "../lib/socialpulse";
import api from "../lib/api";

/* IndianAirports.com auth popup — email/password (Firebase) is the primary
   method; SocialPulse SSO remains as a secondary option for network accounts.
   Registration details are stored in localStorage (tp_pending_reg) and applied
   right after the first successful sign-in (email register or SSO callback). */

const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const YEARS = (() => { const y = new Date().getFullYear() - 13; return Array.from({ length: 95 }, (_, i) => y - i); })();
const POLICIES = [
  ["terms", "Terms of Use", "/legal/terms"],
  ["privacy", "Privacy Policy", "/legal/privacy"],
  ["cookies", "Cookie Policy", "/legal/cookies"],
  ["accessibility", "Accessibility Policy", "/legal/accessibility"],
];

export async function applyPendingRegistration() {
  const raw = localStorage.getItem("tp_pending_reg");
  if (!raw) return;
  try {
    const reg = JSON.parse(raw);
    if (auth?.currentUser) localStorage.setItem("token", await auth.currentUser.getIdToken());
    if (reg.profile) await api.post("/auth/profile", reg.profile).catch(() => toast.warning("We couldn't save your profile details — you can edit them in your profile"));
    if (reg.business?.name) {
      const { data } = await api.post("/marketplace/businesses/register", reg.business);
      toast.success(`Business profile created for ${data.name} — manage it from your profile`);
    }
  } catch { /* profile application is best-effort; user can edit later */ }
  localStorage.removeItem("tp_pending_reg");
}

const Brand = () => <>Airlines<span className="text-blue-600">Airports</span><span className="text-zinc-400 font-400 text-[0.65em]">.com</span></>;

export default function AuthModal({ view }) {
  const nav = useNavigate();
  const isRegister = view === "register";
  const [keep, setKeep] = useState(() => localStorage.getItem("tp_keep") !== "0");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // register state
  const [regTab, setRegTab] = useState("individual");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [gender, setGender] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [loc, setLoc] = useState({ country: "", region: "", city: "" });
  const [locSet, setLocSet] = useState(false);
  const [biz, setBiz] = useState({ name: "", address: "", contact_person: "", business_email: "", business_phone: "" });
  const [accepted, setAccepted] = useState({});

  const allAccepted = POLICIES.every(([k]) => accepted[k]);
  const regReady = !isRegister || (allAccepted && (regTab !== "business" || biz.name.trim().length >= 2));
  const blockReason = !allAccepted ? "Tick every policy box to enable the sign-up buttons"
    : "Enter your business name to continue";

  const setKeepChoice = (v) => { setKeep(v); localStorage.setItem("tp_keep", v ? "1" : "0"); };

  const preparePending = () => {
    if (!isRegister) { localStorage.removeItem("tp_pending_reg"); return; }
    const profile = {
      first_name: first.trim() || undefined, last_name: last.trim() || undefined,
      gender: gender || undefined, birth_year: birthYear || undefined,
      location: locSet || loc.country || loc.city ? loc : undefined,
      account_type: regTab,
    };
    const pending = { profile };
    if (regTab === "business" && biz.name.trim()) {
      pending.business = { ...biz, contact_person: biz.contact_person.trim() || `${first} ${last}`.trim() };
    }
    localStorage.setItem("tp_pending_reg", JSON.stringify(pending));
  };

  const beforeAuth = async () => {
    if (isRegister && !regReady) { toast.error(blockReason); return false; }
    preparePending();
    localStorage.setItem("tp_last_activity", String(Date.now()));
    try { await setPersistence(auth, keep ? browserLocalPersistence : browserSessionPersistence); } catch {}
    return true;
  };

  const fail = (e) => toast.error(e?.code?.replace("auth/", "").replace(/-/g, " ") || e?.message || "Failed");

  const socialpulse = async () => {
    if (!(await beforeAuth())) return;
    setBusy(true);
    try {
      // honour ?next= — the SSO callback returns the user to it after sign-in
      const next = new URLSearchParams(window.location.search).get("next");
      await startSocialPulseLogin(next && next.startsWith("/") ? next : "/dashboard");
    } catch (err) { fail(err); setBusy(false); }
  };

  const emailAuth = async () => {
    if (!(await beforeAuth())) return;
    if (!email.trim()) { toast.error("Enter your email address"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      let cred;
      if (isRegister) {
        cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        const dn = `${first} ${last}`.trim();
        if (dn) await updateProfile(cred.user, { displayName: dn }).catch(() => {});
        localStorage.setItem("token", await cred.user.getIdToken());
        await applyPendingRegistration();
        toast.success("Welcome to AirlinesAirports.com!");
      } else {
        cred = await signInWithEmailAndPassword(auth, email.trim(), password);
        localStorage.setItem("token", await cred.user.getIdToken());
      }
      const next = new URLSearchParams(window.location.search).get("next");
      nav(next && next.startsWith("/") ? next : "/");
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const resetPassword = async () => {
    if (!email.trim()) { toast.error("Enter your email above first, then tap Forgot password"); return; }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      toast.success("Password reset email sent — check your inbox");
    } catch (e) { fail(e); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] flex items-start sm:items-center justify-center overflow-y-auto p-3 sm:p-6"
      data-testid="auth-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) nav("/"); }}>
      <div className="bg-white w-full max-w-lg my-4 sm:my-0 max-h-[92vh] overflow-y-auto border border-zinc-300 shadow-2xl relative">
        <button onClick={() => nav("/")} data-testid="auth-modal-close" aria-label="Close"
          className="absolute top-3 right-3 p-1.5 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 z-10"><X className="w-5 h-5" /></button>

        <div className="px-6 sm:px-8 pt-8 pb-7">
          {/* Header */}
          <h1 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight" data-testid="auth-title">
            {isRegister ? <>Register &amp; Join <Brand /></> : <>Sign in to <Brand /></>}
          </h1>
          <p className="text-xs font-700 text-rose-700 mt-1">Part of the SocialPulse<sup className="font-400">&reg;</sup> network</p>
          <p className="text-sm text-muted-foreground mt-2">
            {isRegister ? "Tell us who you are, then create your account below." : "Sign in with your email, or use your SocialPulse account."}
          </p>

          {!FIREBASE_READY && (
            <div className="mt-4 flex gap-2 items-start text-sm text-amber-800 bg-amber-50 border border-amber-200 p-3" data-testid="firebase-not-ready">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> Sign-in isn't fully configured yet.
            </div>
          )}

          {/* Cross link */}
          <button onClick={() => nav(isRegister ? "/login" : "/register")} data-testid="auth-cross-link"
            className="mt-4 w-full flex items-center justify-between gap-2 border border-zinc-300 bg-zinc-50 hover:border-primary px-4 py-2.5 text-sm transition-colors">
            <span className="font-600 text-left">{isRegister ? "Already have an account?" : <>New to <Brand />?</>}</span>
            <span className="shrink-0 inline-flex items-center gap-1 text-xs font-900 tracking-widest text-primary">{isRegister ? "SIGN / LOG IN" : "REGISTER"} <ArrowRight className="w-3.5 h-3.5" /></span>
          </button>

          {isRegister && (
            <RegisterFields regTab={regTab} setRegTab={setRegTab} first={first} setFirst={setFirst} last={last} setLast={setLast}
              gender={gender} setGender={setGender} birthYear={birthYear} setBirthYear={setBirthYear}
              loc={loc} setLoc={setLoc} locSet={locSet} setLocSet={setLocSet} biz={biz} setBiz={setBiz} />
          )}

          {isRegister && (
            <div className="mt-5 border border-zinc-300 bg-zinc-50/60 p-4" data-testid="auth-policies">
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Please read &amp; accept <span className="normal-case tracking-normal">— every item is required to create your account</span></div>
              {POLICIES.map(([k, label, href]) => (
                <label key={k} className="flex items-start gap-2 py-1 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!accepted[k]} onChange={() => setAccepted((a) => ({ ...a, [k]: !a[k] }))}
                    data-testid={`policy-${k}`} className="w-4 h-4 mt-0.5 accent-primary shrink-0" />
                  <span>I have read and accept the <Link to={href} target="_blank" className="text-primary font-600 underline">{label}</Link>.</span>
                </label>
              ))}
              <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="auth-policies-hint">
                {allAccepted ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Thanks — sign-up buttons are enabled.</span> : "Tick every box to enable the sign-up buttons."}
              </p>
            </div>
          )}

          {/* Email & password — primary sign-in */}
          <div className={`mt-5 ${isRegister && !regReady ? "opacity-50 pointer-events-none select-none" : ""}`} data-testid="auth-methods">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-600 text-zinc-600 mb-1">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email-input"
                  autoComplete="email" placeholder="you@example.com"
                  className="w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-xs font-600 text-zinc-600 mb-1">Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password-input"
                  autoComplete={isRegister ? "new-password" : "current-password"} placeholder={isRegister ? "At least 6 characters" : "Your password"}
                  onKeyDown={(e) => e.key === "Enter" && emailAuth()}
                  className="w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <button onClick={emailAuth} disabled={busy || !FIREBASE_READY} data-testid="email-auth-submit-btn"
                className="w-full flex items-center justify-center gap-2 bg-blue-700 text-white py-3 text-sm font-700 hover:bg-blue-800 transition-colors disabled:opacity-50 rounded-full">
                <Mail className="w-4 h-4" /> {busy ? "Please wait…" : isRegister ? "Create Account" : "Sign In with Email"}
              </button>
              {!isRegister && (
                <button onClick={resetPassword} data-testid="forgot-password-btn"
                  className="w-full text-xs text-primary font-600 hover:underline text-center">Forgot password?</button>
              )}
            </div>

            {/* SocialPulse SSO — secondary */}
            {SOCIALPULSE_SSO_READY && (
              <>
                <div className="flex items-center gap-3 my-4">
                  <span className="flex-1 h-px bg-zinc-200" /><span className="text-[11px] text-zinc-400 font-600 uppercase tracking-widest">or</span><span className="flex-1 h-px bg-zinc-200" />
                </div>
                <button onClick={socialpulse} disabled={busy || !FIREBASE_READY} data-testid="socialpulse-login-btn"
                  className="w-full flex items-center justify-center gap-2 border-2 border-violet-700 bg-white text-violet-700 py-3 text-sm font-700 hover:bg-violet-50 transition-colors disabled:opacity-50 rounded-full">
                  <Sparkles className="w-4 h-4" /> {busy ? "Redirecting…" : "Continue with SocialPulse"}
                </button>
                <p className="text-xs text-muted-foreground text-center mt-2">You'll be redirected to SocialPulse to {isRegister ? "create your account" : "sign in"}, then brought straight back.</p>
              </>
            )}
          </div>

          {/* Keep signed in */}
          <label className="mt-5 flex items-center gap-2 text-sm font-600 cursor-pointer" data-testid="auth-keep-signed-in">
            <input type="checkbox" checked={keep} onChange={(e) => setKeepChoice(e.target.checked)} className="w-4 h-4 accent-primary" />
            Keep me signed in on this device <span className="text-muted-foreground font-400">(12h idle limit)</span>
          </label>

          <p className="mt-4 text-xs text-muted-foreground text-center">{isRegister ? "Your account works across AirlinesAirports.com features — reviews, My Flight, Travel Hub." : "New here? Use Register to create your free account."}</p>
        </div>
      </div>
    </div>
  );
}

function RegisterFields({ regTab, setRegTab, first, setFirst, last, setLast, gender, setGender,
  birthYear, setBirthYear, loc, setLoc, locSet, setLocSet, biz, setBiz }) {
  const inp = "w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary";
  const lbl = "block text-xs font-600 text-zinc-600 mb-1";
  return (
    <div className="mt-5">
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Register as <span className="normal-case tracking-normal">— you can register as both, start with either</span></div>
      <div className="flex border border-zinc-300 mb-4">
        <button onClick={() => setRegTab("individual")} data-testid="regtab-individual"
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-600 ${regTab === "individual" ? "bg-primary text-white" : "bg-white hover:bg-zinc-50"}`}>
          <User className="w-4 h-4" /> Individual
        </button>
        <button onClick={() => setRegTab("business")} data-testid="regtab-business"
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-600 ${regTab === "business" ? "bg-primary text-white" : "bg-white hover:bg-zinc-50"}`}>
          <Building2 className="w-4 h-4" /> Business
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>First name</label><input value={first} onChange={(e) => setFirst(e.target.value)} data-testid="reg-first-name" className={inp} placeholder="First name" /></div>
        <div><label className={lbl}>Last name</label><input value={last} onChange={(e) => setLast(e.target.value)} data-testid="reg-last-name" className={inp} placeholder="Last name" /></div>
        <div><label className={lbl}>Gender</label>
          <select value={gender} onChange={(e) => setGender(e.target.value)} data-testid="reg-gender" className={inp}>
            <option value="">Select…</option>{GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select></div>
        <div><label className={lbl}>Year of birth</label>
          <select value={birthYear} onChange={(e) => setBirthYear(e.target.value)} data-testid="reg-birth-year" className={inp}>
            <option value="">Year</option>{YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select></div>
      </div>

      <div className="mt-4 border border-zinc-200 bg-zinc-50/60 p-3" data-testid="reg-location">
        <div className="text-xs font-600 text-zinc-600 mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" /> Confirm your location</div>
        <div className="flex flex-wrap gap-2">
          <input value={loc.country} onChange={(e) => { setLoc({ ...loc, country: e.target.value }); setLocSet(false); }} data-testid="reg-loc-country" placeholder="United States" className="flex-1 min-w-[110px] border border-zinc-300 bg-white px-3 py-2 text-sm" />
          <input value={loc.region} onChange={(e) => { setLoc({ ...loc, region: e.target.value }); setLocSet(false); }} data-testid="reg-loc-region" placeholder="Oregon" className="flex-1 min-w-[90px] border border-zinc-300 bg-white px-3 py-2 text-sm" />
          <input value={loc.city} onChange={(e) => { setLoc({ ...loc, city: e.target.value }); setLocSet(false); }} data-testid="reg-loc-city" placeholder="Portland" className="flex-1 min-w-[90px] border border-zinc-300 bg-white px-3 py-2 text-sm" />
          <button onClick={() => { setLocSet(true); toast.success("Location saved to your profile"); }} data-testid="reg-loc-set"
            className={`px-4 py-2 text-xs font-900 tracking-widest ${locSet ? "bg-emerald-600 text-white" : "bg-primary text-white hover:bg-blue-700"}`}>
            {locSet ? "SET ✓" : "SET"}
          </button>
        </div>
      </div>

      {regTab === "business" && (
        <div className="mt-4 border border-primary/40 bg-blue-50/40 p-4" data-testid="reg-business-panel">
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-3">Business details <span className="normal-case tracking-normal text-zinc-500">— you can add more from your business profile later</span></div>
          <div className="space-y-3">
            <div><label className={lbl}>Business name <span className="text-rose-600">*</span></label>
              <input value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })} data-testid="reg-biz-name" className={inp} placeholder="e.g. Portland Airport — Indigo Airlines" /></div>
            <div><label className={lbl}>Business address</label>
              <input value={biz.address} onChange={(e) => setBiz({ ...biz, address: e.target.value })} data-testid="reg-biz-address" className={inp} placeholder="Street, city — enough for customers to find you" /></div>
            <div><label className={lbl}>Contact person</label>
              <input value={biz.contact_person} onChange={(e) => setBiz({ ...biz, contact_person: e.target.value })} data-testid="reg-biz-contact" className={inp} placeholder="Auto-filled from your name — edit if different" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Business email</label>
                <input value={biz.business_email} onChange={(e) => setBiz({ ...biz, business_email: e.target.value })} data-testid="reg-biz-email" className={inp} placeholder="hello@portlandairport.com" /></div>
              <div><label className={lbl}>Business phone</label>
                <input value={biz.business_phone} onChange={(e) => setBiz({ ...biz, business_phone: e.target.value })} data-testid="reg-biz-phone" className={inp} placeholder="+1 555 123 4567" /></div>
            </div>
          </div>
          <p className="text-[11px] text-zinc-600 mt-3 italic">You'll sign in with your own email/phone — the business gets its own profile page where you can add team members with Manager or Editor access.</p>
        </div>
      )}
    </div>
  );
}
